/**
 * Bestellübersicht nach § 312j BGB (#273, seit 16.10.2026). Steht zwischen
 * „Plan wählen“ oder „Kaufen“ und der Zahlung bei Mollie.
 *
 * Unmittelbar vor dem Knopf: die Pflichtangaben (Leistung, Endpreis,
 * Laufzeit, Kündigung), AGB und Widerrufsbelehrung, der Hinweis auf die
 * Widerrufsfunktion im Konto (Art. 246a § 1 Abs. 2 Nr. 1 EGBGB), und für
 * Verbraucher ein eigenes, nicht vorausgewähltes Kontrollkästchen für den
 * sofortigen Beginn. Der Knopf heißt „Zahlungspflichtig bestellen“. Erst er
 * ruft billing/checkout; der Server lehnt ohne `bestellt` und, bei
 * Verbrauchern, ohne `sofort_beginn` ab (bestellung.ts).
 *
 * Die Zeilen sagen dasselbe wie `vertragsZeilen` in bestellung.ts, die die
 * Bestätigungsmail schreibt; `ZUSTIMMUNG_TEXT` steht wörtlich in beiden
 * (bestellung.test.ts prüft das).
 *
 * Englisch (seit 16.10.2026): die Zeilen wie `vertragsZeilen(b, 'en')`,
 * die Zustimmung wie `ZUSTIMMUNG_TEXT_EN` auf dem Server. AGB und
 * Widerrufsbelehrung gibt es nur deutsch, Vertragssprache ist Deutsch; die
 * englische Übersicht sagt das.
 */

import { useId, useState } from 'react';
import { starteCheckout, type ApiFehler, type Paket, type Plan, type Status } from '../api';
import { preisAnzeige, SATZ_DE } from '../preise';
import { Hinweis, Kreisel } from '../ui';
import { locale, sprache, t } from '../sprache';

export const AGB_URL = 'https://www.99takes.com/de/agb';
export const WIDERRUF_URL = 'https://www.99takes.com/de/widerrufsbelehrung';

export const ZUSTIMMUNG_TEXT =
  'Ich verlange ausdrücklich, dass 99takes vor Ablauf der Widerrufsfrist mit der Leistung beginnt und die Credits sofort gutschreibt. '
  + 'Mir ist bekannt, dass ich bei vollständiger Vertragserfüllung mein Widerrufsrecht verliere und bei einem Widerruf Wertersatz für bereits genutzte Credits leiste.';

/** Dieselbe Zustimmung auf Englisch, wörtlich wie `ZUSTIMMUNG_TEXT_EN` in bestellung.ts. */
export const ZUSTIMMUNG_TEXT_EN =
  'I expressly request that 99takes begins the service before the withdrawal period ends and credits my account immediately. '
  + 'I am aware that I lose my right of withdrawal once the contract has been fully performed, and that I pay compensation for credits already used if I withdraw.';

export type Auswahl = { art: 'plan'; plan: Plan } | { art: 'paket'; paket: Paket };

const zahl = (n: number) => new Intl.NumberFormat(locale()).format(n);

/** Die Pflichtangaben als Zeilen. Ohne Nettopreis (alte billing-Function) das Preisschild. */
export function bestellZeilen(w: Auswahl, satz: string): Array<[string, string]> {
  if (sprache() === 'en') {
    if (w.art === 'plan') {
      const p = w.plan;
      const preis = p.preis_netto_cent != null ? (() => { const a = preisAnzeige(p.preis_netto_cent, satz); return `${a.haupt} per month (${a.zusatz}), charged in advance`; })() : p.price_label;
      return [
        ['Service', `99takes ${p.name}: ${zahl(p.monthly_credits)} credits per month. A month's credits are valid until the end of the billing period.`],
        ['Price', preis],
        ['Term', 'One month, renewing for one month at a time.'],
        ['Cancellation', 'Any time, effective at the end of the current month, via “Verträge hier kündigen” (cancel contracts) on www.99takes.com or in your account, no login needed.'],
      ];
    }
    const k = w.paket;
    const preis = k.preis_netto_cent != null ? (() => { const a = preisAnzeige(k.preis_netto_cent, satz); return `${a.haupt} one-off (${a.zusatz})`; })() : k.price_label;
    return [
      ['Service', `99takes ${k.name}: ${zahl(k.credits)} credits, one-off. Purchased credits do not expire.`],
      ['Price', preis],
      ['Term', 'None. No subscription, nothing else will be charged.'],
    ];
  }
  if (w.art === 'plan') {
    const p = w.plan;
    const preis = p.preis_netto_cent != null ? (() => { const a = preisAnzeige(p.preis_netto_cent, satz); return `${a.haupt} je Monat (${a.zusatz}), im Voraus abgebucht`; })() : p.price_label;
    return [
      ['Leistung', `99takes ${p.name}: ${zahl(p.monthly_credits)} Credits je Monat. Credits eines Monats gelten bis zum Ende der Abrechnungsperiode.`],
      ['Preis', preis],
      ['Laufzeit', 'Ein Monat, verlängert sich jeweils um einen Monat.'],
      ['Kündigung', 'Jederzeit zum Ende des laufenden Monats, über „Verträge hier kündigen“ auf www.99takes.com oder im Konto, ohne Anmeldung.'],
    ];
  }
  const k = w.paket;
  const preis = k.preis_netto_cent != null ? (() => { const a = preisAnzeige(k.preis_netto_cent, satz); return `${a.haupt} einmalig (${a.zusatz})`; })() : k.price_label;
  return [
    ['Leistung', `99takes ${k.name}: ${zahl(k.credits)} Credits, einmalig. Gekaufte Credits verfallen nicht.`],
    ['Preis', preis],
    ['Laufzeit', 'Keine. Kein Abo, es wird nichts weiter abgebucht.'],
  ];
}

export default function Bestellung({ auswahl, status, abbrechen }: { auswahl: Auswahl; status: Status; abbrechen: () => void }) {
  const satz = status.steuer?.satz ?? SATZ_DE;
  const daten = status.rechnungsdaten ?? null;
  const verbraucher = daten?.art !== 'firma';
  const gesperrt = status.steuer && !status.steuer.erlaubt ? status.steuer.grund ?? t('An deine Anschrift verkaufen wir noch nicht.', 'We do not sell to your address yet.') : null;
  const [zugestimmt, setZugestimmt] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState('');
  const id = useId();
  const name = auswahl.art === 'plan' ? auswahl.plan.name : auswahl.paket.name;

  async function bestellen() {
    setSendet(true);
    setFehler('');
    try {
      const was = auswahl.art === 'plan' ? { plan: auswahl.plan.plan } : { pack: auswahl.paket.pack };
      const { url } = await starteCheckout({ ...was, bestellt: true, sofort_beginn: verbraucher ? zugestimmt : false });
      window.location.assign(url);
    } catch (e) {
      setFehler((e as ApiFehler).message);
      setSendet(false);
    }
  }

  const bereit = !!daten && !gesperrt && (!verbraucher || zugestimmt);

  return (
    <section aria-labelledby={`${id}-titel`} className="mb-8 rounded-karte border border-marke/40 bg-flaeche p-4 shadow-karte ring-1 ring-marke/30 sm:p-6">
      <h2 id={`${id}-titel`} className="m-0 mb-1 font-serif text-[1.375rem] leading-tight font-medium text-tinte">{t('Deine Bestellung:', 'Your order:')} {name}</h2>
      <p className="mt-0 mb-4 text-[0.875rem] text-text-leise">{t('Bitte prüfe die Angaben. Bezahlt wird im nächsten Schritt bei Mollie.', 'Please check the details. You pay at Mollie in the next step.')}</p>

      {!daten && (
        <Hinweis art="schlecht">
          {t(<>Bitte trag zuerst unten deine <a href="#rechnungsdaten" className="underline">Rechnungsdaten</a> ein. Sie stehen auf der Rechnung, und an ihnen hängt die Umsatzsteuer.</>,
            <>Please first enter your <a href="#rechnungsdaten" className="underline">billing details</a> below. They appear on the invoice, and VAT depends on them.</>)}
        </Hinweis>
      )}
      {gesperrt && <Hinweis art="schlecht">{gesperrt}</Hinweis>}
      {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}

      <dl className="m-0 mb-4 text-[0.9375rem]">
        <div className="flex flex-col gap-0.5 border-t border-linie-zart py-2.5 sm:flex-row sm:gap-4">
          <dt className="shrink-0 text-text-leise sm:w-32">{t('Anbieter', 'Provider')}</dt>
          <dd className="m-0 text-text">Tappla UG (haftungsbeschränkt), Ravensburg</dd>
        </div>
        {bestellZeilen(auswahl, satz).map(([k, v]) => (
          <div key={k} className="flex flex-col gap-0.5 border-t border-linie-zart py-2.5 sm:flex-row sm:gap-4">
            <dt className="shrink-0 text-text-leise sm:w-32">{k}</dt>
            <dd className={`m-0 ${k === t('Preis', 'Price') ? 'font-semibold text-tinte' : 'text-text'}`}>{v}</dd>
          </div>
        ))}
      </dl>

      <p className="mt-0 mb-3 text-[0.875rem] leading-relaxed text-text">
        {t(<>
          Es gelten unsere <a href={AGB_URL} target="_blank" rel="noopener" className="text-marke-tief underline">AGB</a>.
          {verbraucher && <> Als Verbraucher hast du ein Widerrufsrecht von 14 Tagen, Einzelheiten in der <a href={WIDERRUF_URL} target="_blank" rel="noopener" className="text-marke-tief underline">Widerrufsbelehrung</a>. Widerrufen kannst du auch online: im Konto unter „Vertrag widerrufen“.</>}
          {!verbraucher && <> Du bestellst als Unternehmen; ein Widerrufsrecht für Verbraucher besteht dann nicht.</>}
        </>, <>
          Our <a href={AGB_URL} target="_blank" rel="noopener" className="text-marke-tief underline">terms (German)</a> apply.
          {verbraucher && <> As a consumer, you have a 14-day right of withdrawal, details in the <a href={WIDERRUF_URL} target="_blank" rel="noopener" className="text-marke-tief underline">withdrawal policy (German)</a>. You can also withdraw online: in your account under “Withdraw from contract”.</>}
          {!verbraucher && <> You are ordering as a business; the consumer right of withdrawal does not apply.</>}
          {' '}The terms and the withdrawal policy are only available in German. The contract language is German.
        </>)}
      </p>

      {verbraucher && daten && (
        <label className="mb-4 flex cursor-pointer items-start gap-2.5 rounded-kasten border border-linie bg-ebene p-3 text-[0.875rem] leading-relaxed text-text">
          <input type="checkbox" checked={zugestimmt} onChange={e => setZugestimmt(e.target.checked)} className="mt-1 size-4 shrink-0 accent-marke" />
          <span>{t(ZUSTIMMUNG_TEXT, ZUSTIMMUNG_TEXT_EN)}</span>
        </label>
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
        <button type="button" onClick={abbrechen} disabled={sendet} className="h-11 cursor-pointer rounded-full border border-linie bg-flaeche px-5 text-[0.875rem] font-semibold text-text hover:border-linie-stark disabled:opacity-60">
          {t('Zurück', 'Back')}
        </button>
        <button
          type="button" onClick={() => void bestellen()} disabled={!bereit || sendet}
          className="h-11 cursor-pointer rounded-full border-0 bg-knopf px-6 text-[0.9375rem] font-semibold text-auf-marke hover:bg-knopf-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {sendet ? <span className="inline-flex items-center gap-2"><Kreisel className="size-3.5" />{t('Einen Moment …', 'One moment …')}</span> : t('Zahlungspflichtig bestellen', 'Order with obligation to pay')}
        </button>
      </div>
      {verbraucher && daten && !zugestimmt && (
        <p className="mt-2 mb-0 text-right text-[0.8125rem] text-text-hell">{t('Bestellen geht, sobald du dem sofortigen Beginn zugestimmt hast.', 'You can order once you have agreed to the immediate start.')}</p>
      )}
    </section>
  );
}
